/**
 * Formatting used by the shell. Delegates to `@apos/shared` format helpers and falls back to a
 * local implementation if a shared function throws (e.g. while the shared lane is still a stub).
 * Feature lanes may call the shared helpers directly.
 */
import * as shared from '@apos/shared';
import type { DataQuality, EpochMs, Freshness } from '@apos/shared';
import { zoneAbbrev, zonedParts } from './time';

function attempt<T>(fn: () => T, fallback: () => T): T {
  try {
    const v = fn();
    return v === undefined ? fallback() : v;
  } catch {
    return fallback();
  }
}

const pad = (n: number, w = 2) => String(Math.trunc(Math.abs(n))).padStart(w, '0');
const nf0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
export const DASH = '—';

export const fmt = {
  distance: (m: number | null) =>
    attempt(
      () => shared.formatDistance(m),
      () => (m === null || !Number.isFinite(m) ? DASH : m >= 926 ? `${nf1.format(m / 1852)} NM` : `${nf0.format(m / 0.3048)} ft`),
    ),
  altitude: (ft: number | null) => attempt(() => shared.formatAltitude(ft), () => (ft === null ? DASH : `${nf0.format(ft)} ft`)),
  bearing: (deg: number | null) =>
    attempt(() => shared.formatBearing(deg), () => (deg === null ? DASH : `${pad(Math.round(((deg % 360) + 360) % 360) % 360, 3)}°`)),
  speed: (kt: number | null) => attempt(() => shared.formatSpeed(kt), () => (kt === null ? DASH : `${nf0.format(kt)} kt`)),
  vs: (fpm: number | null) =>
    attempt(
      () => shared.formatVs(fpm),
      () => (fpm === null ? DASH : Math.abs(fpm) < 100 ? 'level' : `${fpm > 0 ? '+' : '−'}${nf0.format(Math.abs(fpm))} fpm`),
    ),
  duration: (sec: number | null) =>
    attempt(
      () => shared.formatDuration(sec),
      () => {
        if (sec === null || !Number.isFinite(sec)) return DASH;
        const s = Math.max(0, Math.round(sec));
        if (s < 60) return `${s} s`;
        if (s < 3600) return `${Math.floor(s / 60)} min ${pad(s % 60)} s`;
        return `${Math.floor(s / 3600)} h ${pad(Math.floor((s % 3600) / 60))} min`;
      },
    ),
  age: (sec: number | null) =>
    attempt(() => shared.formatAge(sec), () => (sec === null ? DASH : `${fmt.durationShort(sec)} ago`)),
  durationShort: (sec: number) => {
    const s = Math.max(0, Math.round(sec));
    if (s < 60) return `${s} s`;
    if (s < 3600) return `${Math.floor(s / 60)} min`;
    return `${Math.floor(s / 3600)} h ${pad(Math.floor((s % 3600) / 60))}`;
  },
  utc: (t: EpochMs, withSeconds = true) =>
    attempt(
      () => shared.formatUtc(t, withSeconds),
      () => {
        const d = new Date(t);
        return `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}${withSeconds ? `:${pad(d.getUTCSeconds())}` : ''}Z`;
      },
    ),
  local: (t: EpochMs, tz: string, withSeconds = true) =>
    attempt(
      () => shared.formatLocal(t, tz, withSeconds),
      () => {
        const p = zonedParts(t, tz);
        return `${pad(p.hour)}:${pad(p.minute)}${withSeconds ? `:${pad(p.second)}` : ''} ${zoneAbbrev(t, tz)}`;
      },
    ),
  /** "Tue 29 Sep" in the zone. */
  date: (t: EpochMs, tz: string) =>
    new Intl.DateTimeFormat('en-GB', { timeZone: tz, weekday: 'short', day: '2-digit', month: 'short' }).format(new Date(t)),
  freshness: (f: Freshness) =>
    attempt(() => shared.freshnessLabel(f), () => ({ live: 'Live', aging: 'Aging', stale: 'Stale', lost: 'Lost', none: 'No data' })[f]),
  quality: (q: DataQuality) =>
    attempt(
      () => shared.qualityLabel(q),
      () => ({ observed: 'Observed', interpolated: 'Interpolated', estimated: 'Estimated', predicted: 'Predicted', manual: 'Manual' })[q],
    ),
  compass: (deg: number) =>
    attempt(
      () => shared.compassPoint(deg),
      () => ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'][Math.round((((deg % 360) + 360) % 360) / 22.5) % 16]!,
    ),
  /** Plain number with thousands separators. */
  int: (n: number | null) => (n === null || !Number.isFinite(n) ? DASH : nf0.format(n)),
  /** Signed ms offset, "+12 ms" / "−1.2 s". */
  offset: (ms: number) => {
    const sign = ms >= 0 ? '+' : '−';
    const a = Math.abs(ms);
    return a >= 1000 ? `${sign}${nf1.format(a / 1000)} s` : `${sign}${nf0.format(a)} ms`;
  },
};

/** Call a shared function, returning `fallback` if it throws (stub-safe). */
export function trySafe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}
